import { LongLayoverIcon, ShortLayoverIcon } from '@/src/assets/svgs/LayoverIcons'
import { PerAdultLabel } from '@/src/components/common/srp/perAdultLabel'
import { SmartMetricsBadge } from '@/src/components/common/srp/smartMetrics'
import useFormatCurrencyForSrp from '@/src/hooks/currency/useFormatCurrencyForSrp'
import useFormatDate from '@/src/hooks/date/useFormatDate'
import { useFormatTime } from '@/src/hooks/srp/formatTime'
import useSearchResults from '@/src/hooks/srp/useSearchResults'
import { TranslationFunction } from '@/src/types/translations'
import { getAirlineIcons } from '@/src/utils/confirmation/airlineUtils'
import { getFormattedDate } from '@/src/utils/date'
import { replacePlaceHolderString } from '@/src/utils/generalUtils/replacePlaceholder'
import { ProcessedFlightMetrics } from '@/src/utils/srp/aiRecommendation/metrics'
import type { ExtractedFlightCardProps } from '@/src/utils/srp/flightCardProps'
import CircleIcon from '@mui/icons-material/Circle'
import NoLuggageOutlinedIcon from '@mui/icons-material/NoLuggageOutlined'
import { Box, Button, CardContent, Tooltip, Typography } from '@mui/material'
import { useTranslations } from 'next-intl'
import React, { useMemo } from 'react'

interface FlightCardProps extends ExtractedFlightCardProps {
    smartMetrics?: ProcessedFlightMetrics
    onBook: () => void
    onViewDetails?: () => void
    tCommon: TranslationFunction<'common'>
    tFlightDetails: TranslationFunction<'srpFlightDetails'>
    isBookingLoading?: boolean
    isDisabled?: boolean
    buttonText?: string
}

const FlightCard: React.FC<FlightCardProps> = ({
    smartMetrics,
    onBook,
    onViewDetails,
    isBookingLoading = false,
    isDisabled = false,
    airlineName,
    airlineCodes,
    departureTime,
    arrivalTime,
    stops,
    totalDuration,
    price = '100',
    via,
    discount,
    layovers,
    seatsLeft,
    strikeOffPrice,
    arrivalAirport,
    departureAirport,
    noBaggage,
    coupon_detail,
    tCommon,
    tFlightDetails,
    isNbDeparture,
    isNbArrival,
    arrivalDate,
    departureDate,
    buttonText
}) => {
    const formatTime = useFormatTime()
    const { isPerPaxPricingEnabled, jsons } = useSearchResults()
    const duration = formatTime(totalDuration)
    const departureAirportName = useMemo(() => {
        return jsons?.ap?.[departureAirport?.toUpperCase()]?.n || ''
    }, [departureAirport, jsons])
    const arrivalAirportName = useMemo(() => {
        return jsons?.ap?.[arrivalAirport?.toUpperCase()]?.n || ''
    }, [arrivalAirport, jsons])

    const { formatCurrency } = useFormatCurrencyForSrp()
    const t = useTranslations('srpFlightDetails')
    const { formatDate } = useFormatDate()
    const isNextDayCallOut = departureDate !== arrivalDate

    const couponDetails = coupon_detail?.discountAmount
        ? {
            ...coupon_detail,
            discountedPrice: formatCurrency(coupon_detail.discountedPrice),
            discountAmount: formatCurrency(coupon_detail.discountAmount)
        }
        : {}

    function getLayoverTypeAndMessage (layoverMinutes: number) {
        if (layoverMinutes > 240) {
            return {
                type: 'longLayover',
                message: t('longLayoverCta'),
                iconColor: 'warning.main'
            }
        }
        return {
            type: 'shortLayover',
            message: t('shortLayoverCta'),
            iconColor: 'info.main'
        }
    }
    return (
        <Box
            sx={{
                p: 0,
                bgcolor: 'white',
                cursor: 'pointer',

                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                    borderColor: 'grey.300'
                }
            }}
            width={'820px'}
        >
            <CardContent sx={{ p: 0, '&:last-child': { pb: 0 }, minWidth: 'max-content' }}>

                <Box display="flex" alignItems="flex-start" justifyContent="space-between">
                    {/* Left section - Flight details */}
                    <Box display="flex" alignItems="center" gap={1} flex={1}>
                        {/* Airline logo */}
                        <Box
                            display="flex"
                            alignItems="center"
                            flexDirection="column"
                            width="100px"
                            sx={{ overflowX: 'wrap', wordBreak: 'break-word' }}
                        >
                            <Box display={'flex'} maxWidth={150} overflow={'auto'} gap={0.5}>
                                {getAirlineIcons(airlineCodes, 32, 32)}
                            </Box>
                            <Typography textAlign="center" variant="body2" color="grey.600">
                                {airlineName}
                            </Typography>
                        </Box>

                        {/* Flight info */}
                        <Box flex={1}>
                            {/* Time and route */}
                            <Box display="flex" alignItems="flex-end" gap={1}>
                                <Box display="flex" alignItems="center" flexDirection="column" width="60px">
                                    <Typography
                                        variant="h6"
                                        component="span"
                                        sx={{
                                            fontSize: '1.125rem',
                                            fontWeight: 700,
                                            color: 'black',
                                            display: 'flex',
                                            flexDirection: 'row'
                                        }}
                                    >
                                        {departureTime}
                                    </Typography>
                                    {isNbDeparture ? (
                                        <Tooltip
                                            title={
                                                <Box>
                                                    <Typography fontWeight={600} color="inherit" fontSize={"12px"}>
                                                        {t('nearbyAirport')}
                                                    </Typography>
                                                    <Typography fontWeight={500} color="inherit" fontSize={"10px"}>{departureAirport}- {departureAirportName}</Typography>
                                                    <Typography color="inherit" sx={{ mt: 0.5 }} fontSize={"10px"} fontWeight={400}>
                                                        {t('nearbyAirportCta')}
                                                    </Typography>
                                                </Box>
                                            }
                                            placement="top"
                                        >
                                            <Typography
                                                variant="body2"
                                                fontWeight={600}
                                                sx={{
                                                    color: '#184E91',
                                                    background: '#EFF6FF',
                                                    px: 1,
                                                    borderRadius: 1,
                                                    display: 'inline-block',
                                                    cursor: 'help'
                                                }}
                                            >
                                                {departureAirport}
                                            </Typography>
                                        </Tooltip>
                                    ) : (
                                        <Typography
                                            variant="body2"
                                            fontWeight={600}
                                            sx={{
                                                color: 'grey.600',
                                                display: 'inline-block'
                                            }}
                                        >
                                            {departureAirport}
                                        </Typography>
                                    )}
                                </Box>
                                <Box display="flex" alignItems="center" flexDirection="column">
                                    <Typography fontSize="12px" variant="body2" color="grey.600">
                                        {duration}
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mx: 1 }}>
                                        <CircleIcon sx={{ fontSize: 6, color: 'grey.600' }} />
                                        <Box height="1px" width="142px" bgcolor="grey.600" />
                                        <CircleIcon sx={{ fontSize: 6, color: 'grey.600' }} />
                                    </Box>
                                    <Box display="flex" alignItems="center" flexDirection="row">
                                        <Typography
                                            fontSize="12px"
                                            variant="body2"
                                            color={stops === 0 ? ' #16a34a' : 'grey.600'}
                                            fontWeight={stops === 0 ? 600 : 400}
                                        >
                                            {stops === 0
                                                ? tCommon('direct')
                                                : `${stops} ${stops > 1 ? tCommon('stops', { s: 's' }) : tCommon('stops', { s: '' })}`}
                                        </Typography>
                                        {via && (
                                            <>
                                                <CircleIcon sx={{ fontSize: 5, color: 'grey.600', mx: 0.5 }} />
                                                <Typography
                                                    component="span"
                                                    sx={{
                                                        fontSize: '12px',
                                                        fontWeight: 400,
                                                        color: 'textColors.primary'
                                                    }}
                                                >
                                                    {via}
                                                </Typography>
                                            </>
                                        )}
                                        {/* Layover Icon (if present) */}
                                        {Array.isArray(layovers) &&
                                            layovers.length > 0 &&
                                            (() => {
                                                const layover = layovers[0]
                                                const { type } = getLayoverTypeAndMessage(layover.durationMins)
                                                const LayoverIcon =
                                                    type === 'longLayover' ? LongLayoverIcon : ShortLayoverIcon
                                                return (
                                                    <Tooltip
                                                        title={
                                                            <Box>
                                                                <Typography fontWeight={600} fontSize={"12px"}>{t(type)}</Typography>
                                                                <Typography fontWeight={400} fontSize={"10px"}>
                                                                    {t('layover')}: {layover.durationStr} in{' '}
                                                                    {layover.city}
                                                                </Typography>
                                                                <Box display="flex" alignItems="center">
                                                                    <Typography fontWeight={400} fontSize={"10px"}>
                                                                        {
                                                                            getLayoverTypeAndMessage(
                                                                                layover.durationMins
                                                                            ).message
                                                                        }
                                                                    </Typography>
                                                                </Box>
                                                            </Box>
                                                        }
                                                        placement="top"
                                                    >
                                                        <Box
                                                            display="inline-flex"
                                                            alignItems="center"
                                                            sx={{ cursor: 'help', ml: 1 }}
                                                        >
                                                            <LayoverIcon />
                                                        </Box>
                                                    </Tooltip>
                                                )
                                            })()}

                                        {noBaggage && (
                                            <Tooltip
                                                title={
                                                    <Box>
                                                        <Typography fontWeight={600} color="inherit" fontSize={"12px"}>
                                                            {t('noBaggageTitle')}
                                                        </Typography>
                                                        <Typography color="inherit" sx={{ mt: 0.5 }} fontWeight={400} fontSize={"10px"}>
                                                            {t('noBaggageCta')}
                                                        </Typography>
                                                    </Box>
                                                }
                                                placement="top"
                                            >
                                                <Box sx={{ display: 'inline-flex', cursor: 'help', ml: 1, color: 'grey.600' }}>
                                                    <NoLuggageOutlinedIcon fontSize='small' color='inherit' />
                                                </Box>
                                            </Tooltip>
                                        )}
                                    </Box>
                                </Box>
                                <Box display="flex">
                                    <Box display="flex" alignItems="center" flexDirection="column" width="60px">
                                        <Typography
                                            variant="h6"
                                            component="span"
                                            sx={{
                                                fontSize: '1.125rem',
                                                fontWeight: 700,
                                                color: 'black',
                                                display: 'flex',
                                                flexDirection: 'row'
                                            }}
                                        >
                                            {arrivalTime}
                                        </Typography>
                                        {isNbArrival ? (
                                            <Tooltip
                                                title={
                                                    <Box>
                                                        <Typography fontWeight={600} color="inherit" fontSize={"12px"}>
                                                            {tCommon('nearbyAirport')}
                                                        </Typography>
                                                        <Typography fontWeight={500} color="inherit" fontSize={"10px"}>{arrivalAirport}- {arrivalAirportName}</Typography>
                                                        <Typography color="inherit" sx={{ mt: 0.5 }} fontWeight={400} fontSize={"10px"}>
                                                            {t('nearbyAirportCta')}
                                                        </Typography>
                                                    </Box>
                                                }
                                                placement="top"

                                            >
                                                <Typography
                                                    variant="body2"
                                                    fontWeight={600}
                                                    sx={{
                                                        color: '#184E91',
                                                        background: '#EFF6FF',
                                                        px: 1,
                                                        borderRadius: 1,
                                                        display: 'inline-block',
                                                        cursor: 'help'
                                                    }}
                                                >
                                                    {arrivalAirport}
                                                </Typography>
                                            </Tooltip>
                                        ) : (
                                            <Typography
                                                variant="body2"
                                                fontWeight={600}
                                                sx={{
                                                    color: 'grey.600',
                                                    display: 'inline-block'
                                                }}
                                            >
                                                {arrivalAirport}
                                            </Typography>
                                        )}
                                    </Box>
                                    <Box minWidth={'40px'} gap={0.5} px={0.5} display="flex" flexDirection={'column'}>
                                        {isNextDayCallOut && (
                                            <Typography
                                                variant="body2"
                                                fontSize={12}
                                                px={1}
                                                py={0.3}
                                                fontWeight={600}
                                                color="#184E91"
                                                alignItems="center"
                                                // pt={0.5}
                                                bgcolor="#EFF6FF"
                                                borderRadius={1}
                                            >
                                                {formatDate(getFormattedDate(arrivalDate, 'MM/DD/YYYY'), 'D MMM')}
                                            </Typography>
                                        )}

                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    </Box>

                    {/* Right section - Price and actions */}
                    <Box textAlign="right"
                        // ml={1}
                        flexShrink={0}>
                        <Box display="flex" alignItems="center" gap={2} mb={0.5}>
                            {/* Price section */}
                            <Box textAlign="right">
                                <Box display="flex" alignItems="center" gap={1} justifyContent={'flex-end'}>
                                    {/* Seats Left Callout */}
                                    {typeof seatsLeft === 'number' && seatsLeft > 0 && seatsLeft <= 5 && (
                                        <Box
                                            component="span"
                                            sx={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                bgcolor: '#fff0f0',
                                                color: '#c62828',
                                                borderRadius: 1,
                                                fontSize: 12,
                                                fontWeight: 500,
                                                px: 1.2,
                                                py: 0.2,
                                                ml: 1,
                                                height: 24,
                                                lineHeight: 1
                                            }}
                                        >
                                            {seatsLeft}{' '}
                                            {seatsLeft > 1
                                                ? tFlightDetails('seatLeft', { s: 's' })
                                                : tFlightDetails('seatLeft', { s: '' })}
                                        </Box>
                                    )}
                                    <Typography
                                        variant="h4"
                                        sx={{
                                            fontSize: '1.125rem',
                                            fontWeight: 700,
                                            color: 'black',
                                            lineHeight: 1.2
                                        }}
                                    >
                                        {formatCurrency(Number(price))} <PerAdultLabel />
                                    </Typography>
                                </Box>
                                {typeof strikeOffPrice === 'number' && strikeOffPrice > Number(price) && (
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: 'grey.500',
                                            textDecoration: 'line-through',
                                            fontWeight: 400,
                                            fontSize: '0.75rem',
                                            mb: 0.5
                                        }}
                                    >
                                        {formatCurrency(strikeOffPrice)}
                                    </Typography>
                                )}
                                {coupon_detail?.discountAmount && coupon_detail?.discountAmount > 0 && (
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            fontSize: '0.75rem',
                                            color: '#16a34a',
                                            fontWeight: 500
                                        }}
                                    >
                                        {replacePlaceHolderString(
                                            isPerPaxPricingEnabled
                                                ? coupon_detail?.adultCouponMessage
                                                : coupon_detail?.message,
                                            couponDetails
                                        )}
                                    </Typography>
                                )}
                            </Box>

                            <Box>
                                {/* Book button */}
                                <Button
                                    variant="customVariantPrimary"
                                    onClick={() => onBook()}
                                    disabled={isBookingLoading || isDisabled}
                                    sx={{
                                        // bgcolor: isBookingLoading ? '#FFD700' : isDisabled ? 'grey.300' : 'brand.primary',
                                        // color: isBookingLoading
                                        //     ? '#2A5298' // Blue text on gold
                                        //     : 'brand.primaryForeground',
                                        px: '1rem',
                                        py: '0.25rem',
                                        borderRadius: 1,
                                        fontSize: '0.75rem',
                                        fontWeight: 700,
                                        textTransform: 'none',
                                        // minWidth: 100,
                                        minHeight: 0,
                                        height: 40,
                                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                        // ...(isBookingLoading && {
                                        //     bgcolor: '#FFD700 !important', // Force gold color with !important
                                        //     color: '#2A5298 !important',
                                        //     animation: 'pulse 1.5s cubic-bezier(0.4, 0, 0.6, 1) infinite',
                                        //     '@keyframes pulse': {
                                        //         '0%, 100%': { opacity: 1 },
                                        //         '50%': { opacity: 0.8 }
                                        //     }
                                        // }),
                                        '&:hover':
                                            isBookingLoading || isDisabled
                                                ? {}
                                                : {
                                                    bgcolor: 'brand.primaryHover',
                                                    color: 'apple.black',
                                                    transform: 'translateY(-1px)',
                                                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                                                },
                                        // '&.Mui-disabled': isBookingLoading
                                        //     ? {
                                        //         bgcolor: '#FFD700 !important',
                                        //         color: '#2A5298 !important'
                                        //     }
                                        //     : {}
                                    }}
                                >
                                    {isBookingLoading ? tCommon('Loading') : buttonText || tCommon('bookNow')}
                                </Button>
                                {/* Flight details link */}
                                {onViewDetails && (
                                    <Box textAlign="right">
                                        <Button
                                            variant="text"
                                            onClick={() => onViewDetails()}
                                            sx={{
                                                color: 'apple.blue',
                                                fontSize: '0.75rem',
                                                fontWeight: 500,
                                                textTransform: 'none',
                                                p: 0,
                                                minWidth: 'auto',
                                                '&:hover': {
                                                    textDecoration: 'underline',
                                                    bgcolor: 'transparent'
                                                }
                                            }}
                                        >
                                            {tCommon('flightDetails')} →
                                        </Button>
                                    </Box>
                                )}
                            </Box>
                        </Box>


                    </Box>
                </Box>
                {smartMetrics && (

                    <SmartMetricsBadge metrics={smartMetrics} />

                )}

            </CardContent>
        </Box>
    )
}

export { FlightCard }
export type { FlightCardProps }
